import { useState, useCallback, useEffect } from "react";
import Sidebar from "@/components/Sidebar";
import HeroSection from "@/components/HeroSection";
import PortfolioSection from "@/components/PortfolioSection";
import ServicesSection from "@/components/ServicesSection";
import AboutSection from "@/components/AboutSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import FooterSection from "@/components/FooterSection";
import ShopSection from "@/components/ShopSection";
import SectionDivider from "@/components/SectionDivider";
import { useTheme } from "@/components/ThemeProvider";
import SEO from "@/components/SEO";

const Index = () => {
  const [activeSection, setActiveSection] = useState("hello");
  const { mode } = useTheme();

  const handleNavigate = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `/#${id}`);
    }
  }, []);

  // Scroll to hash on initial load
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
      }
    }
  }, []);

  // Update hash and active section on scroll
  useEffect(() => {
    const sections = ["hello", "portfolio", "about", "services", "shop", "testimonials"];
    const observers: IntersectionObserver[] = [];

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
            // Only update hash when we're actually on the homepage
            if (window.location.pathname === "/") {
              window.history.replaceState(null, "", `/#${id}`);
            }
          }
        },
        { threshold: 0.25 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <div
      className="min-h-screen transition-colors duration-700 ease-in-out"
      style={{
        backgroundColor: mode === "dark" ? "hsl(0, 0%, 9%)" : "hsl(45, 100%, 98%)",
      }}
    >
      <SEO
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "KEISH",
              url: "https://keish.studio",
              description: "UX-focused web design studio creating fast, conversion-driven websites for creatives and ambitious founders.",
            },
            {
              "@type": "SiteNavigationElement",
              name: ["Home", "Case Studies", "About", "Services", "Shop", "Contact"],
              url: [
                "https://keish.studio/",
                "https://keish.studio/work",
                "https://keish.studio/about",
                "https://keish.studio/services",
                "https://keish.studio/shop",
                "https://keish.studio/contact",
              ],
            },
          ],
        }}
      />
      <Sidebar activeSection={activeSection} onNavigate={handleNavigate} />

      <main className="lg:ml-[224px]">
        <HeroSection />
        <SectionDivider />
        <PortfolioSection />
        <SectionDivider />
        <AboutSection />
        <ServicesSection />
        <ShopSection />
        <TestimonialsSection />

        <FooterSection />
      </main>
    </div>
  );
};

export default Index;
